import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { DialogControl, FieldControl } from "spark-core-dx/components";
import { SystemTypes } from "spark-core-dx/services";
import {
  asArray,
  dedupeTransforms,
  MATH_OPERATORS,
  normalizeDataTypeLabel,
  TRANSFORM_KIND_CUSTOM,
  TRANSFORM_KIND_ENTITY_LOOKUP,
  TRANSFORM_KIND_MATH,
  TRANSFORM_KIND_PREDEFINED,
  TRANSFORM_KIND_SOURCE_LOOKUP,
} from "./utils";

const CUSTOM_KEY = "custom";
const LOOKUP_SOURCE_KEY = "lookup_source";
const LOOKUP_ENTITY_KEY = "lookup_entity";
const DEFAULT_CUSTOM_TRANSFORM_SCRIPT = `if (value == null)
    return null;
// TODO: Transform incoming "value".
// Return the final mapped value.
return value; // Replace with your transformed value.`;

const LOOKUP_MATCH_OPTIONS = [
  { value: "==", label: "Exact Match" },
  { value: "StartsWith", label: "Starts With" },
  { value: "Contains", label: "Contains" },
];

const LOOKUP_NO_MATCH_OPTIONS = [
  { value: "ReturnNull", label: "Return Null" },
  { value: "KeepOriginal", label: "Keep Original" },
  { value: "Throw", label: "Throw Error" },
];

const LOOKUP_MULTI_MATCH_OPTIONS = [
  { value: "First", label: "Use First Match" },
  { value: "Throw", label: "Throw Error" },
];

const MATH_OPERATORS_REQUIRING_OPERAND = new Set(["Add", "Subtract", "Multiply", "Divide", "Modulo", "Min", "Max", "Coalesce"]);
const MATH_OPERATORS_REQUIRING_SCALE = new Set(["Round", "ImpliedDecimal"]);

const TriangleIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M5 3.5 11 8l-6 4.5z" fill="currentColor" />
  </svg>
);

const ArrowUpIcon = () => (
  <svg className="efmd-transform-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M8 3.2 3.8 7.4l.9.9L7.35 5.65V13h1.3V5.65l2.65 2.65.9-.9z" fill="currentColor" />
  </svg>
);

const ArrowDownIcon = () => (
  <svg className="efmd-transform-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M7.35 3v7.35L4.7 7.7l-.9.9L8 12.8l4.2-4.2-.9-.9-2.65 2.65V3z" fill="currentColor" />
  </svg>
);

const TrashIcon = () => (
  <svg className="efmd-transform-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M6.2 2.2h3.6l.5 1.2h2.4v1.2H3.3V3.4h2.4l.5-1.2Zm-1.7 3.3h7l-.4 8.1c0 .6-.5 1-1.1 1H6c-.6 0-1.1-.4-1.1-1l-.4-8.1Zm2 1.2.2 6.3h1.1l-.2-6.3H6.5Zm2 0L8.3 13h1.1l.2-6.3H8.5Z" fill="currentColor" />
  </svg>
);

const WRAPPED_ARROW_IIFE_REGEX = /^\s*\(\s*\(\s*\)\s*=>\s*\{([\s\S]*)\}\s*\)\s*\(\s*\)\s*;?\s*$/;
const WRAPPED_FUNCTION_IIFE_REGEX = /^\s*\(\s*function\s*\(\s*\)\s*\{([\s\S]*)\}\s*\)\s*\(\s*\)\s*;?\s*$/;

const toEditableScriptBody = (script) => {
  const text = String(script || "");
  if (!text.trim()) return "";
  const arrowMatch = text.match(WRAPPED_ARROW_IIFE_REGEX);
  if (arrowMatch && arrowMatch[1] != null) return String(arrowMatch[1]).trim();
  const fnMatch = text.match(WRAPPED_FUNCTION_IIFE_REGEX);
  if (fnMatch && fnMatch[1] != null) return String(fnMatch[1]).trim();
  return text;
};

const toStoredWrappedScript = (body) => {
  const text = String(body || "");
  if (!text.trim()) return "";
  if (WRAPPED_ARROW_IIFE_REGEX.test(text) || WRAPPED_FUNCTION_IIFE_REGEX.test(text)) {
    return text.trim();
  }
  return `(() => {\n${text.trim()}\n})();`;
};

const findFieldMetadata = (metadata, fieldName) => {
  const fields = Array.isArray(metadata?.Fields) ? metadata.Fields : [];
  const target = String(fieldName || "").trim().toLowerCase();
  return fields.find((field) => String(field?.FieldName || "").trim().toLowerCase() === target) || null;
};

const getHeaderFieldLabel = (field, fallbackConstant = false) => {
  if (fallbackConstant) return <span className="efmd-transform-header-field-name">(constant)</span>;
  const displayName = field?.displayName || field?.fieldName || "";
  const dataType = normalizeDataTypeLabel(field?.dataTypeName || field?.dataTypeId || "");
  if (!dataType) return <span className="efmd-transform-header-field-name">{displayName}</span>;
  return (
    <>
      <span className="efmd-transform-header-field-name">{displayName}</span>{" "}
      <span className="efmd-transform-header-field-type">({dataType})</span>
    </>
  );
};

const buildSearchSelectMetadata = ({ fieldName, displayName, dataJson, placeHolder = "Please Select" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SearchSelectList,
  DataJson: dataJson,
  PlaceHolder: placeHolder,
});

const buildSelectListMetadata = ({ fieldName, displayName, dataJson, placeHolder = "Please Select" }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.SelectList,
  DataJson: dataJson,
  PlaceHolder: placeHolder,
});

const buildPositiveIntegerMetadata = ({ fieldName, displayName }) => ({
  FieldName: fieldName,
  DisplayName: displayName,
  FieldType: SystemTypes.UIFieldType.TextBox,
  ValidationType: SystemTypes.UIFieldValidationType.IntPos,
});

const toSelectData = (items, getValue, getText) =>
  asArray(items).map((item) => ({
    Value: getValue(item),
    Text: getText(item),
  }));

const dedupeById = (items) => {
  const seen = new Set();
  return asArray(items).filter((item) => {
    const key = String(item?.id || item?.Value || item?.value || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getMathConstantLabel = (operator) => {
  switch (operator) {
    case "Add":
      return "Added To";
    case "Subtract":
      return "Subtracted From";
    case "Multiply":
      return "Multiplied By";
    case "Divide":
      return "Divided By";
    case "Modulo":
      return "Modulo By";
    case "Min":
    case "Max":
      return "Compare With";
    case "Coalesce":
      return "Fallback Value";
    default:
      return "Constant";
  }
};

const getMathConstantPlaceholder = (operator) => {
  switch (operator) {
    case "Multiply":
      return "Optional multiplier, e.g. 100";
    case "Divide":
    case "Modulo":
      return "Optional divisor";
    case "Coalesce":
      return "Optional fallback";
    case "Min":
    case "Max":
      return "Optional comparison value";
    default:
      return "Optional number";
  }
};

const clampImportance = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 255;
  const rounded = Math.round(parsed);
  if (rounded < 1) return 1;
  if (rounded > 255) return 255;
  return rounded;
};

const getLookupObjectType = (step, kind) => {
  if (kind === LOOKUP_ENTITY_KEY) return "EntityObject";
  if (kind === LOOKUP_SOURCE_KEY) return "SourceObject";
  return step?.lookupObjectType || step?.LookupObjectType || null;
};

const normalizeStep = (step, index) => {
  if (!step) return null;
  const key = step.key || step.transformKey || step.TransformKey || "";
  const rawCode = step.code || step.customTransformScript || step.CustomTransformScript || step.transformCode || step.TransformCode || "";
  const code = toEditableScriptBody(rawCode);
  const incomingKind = step.kind || null;
  const transformKind =
    step.transformKind ||
    step.TransformKind ||
    step.raw?.TransformKind ||
    step.raw?.transformKind ||
    (step.LookupEntityObjectId || step.lookupEntityObjectId
      ? TRANSFORM_KIND_ENTITY_LOOKUP
      : step.LookupSourceObjectId || step.lookupSourceObjectId
        ? TRANSFORM_KIND_SOURCE_LOOKUP
        : "") ||
    (String(step.lookupObjectType || step.LookupObjectType || "").toLowerCase() === "entityobject"
      ? TRANSFORM_KIND_ENTITY_LOOKUP
      : String(step.lookupObjectType || step.LookupObjectType || "").toLowerCase() === "sourceobject"
        ? TRANSFORM_KIND_SOURCE_LOOKUP
        : "");
  const kind =
    incomingKind ||
    (transformKind === TRANSFORM_KIND_ENTITY_LOOKUP
      ? LOOKUP_ENTITY_KEY
      : transformKind === TRANSFORM_KIND_SOURCE_LOOKUP
        ? LOOKUP_SOURCE_KEY
        : transformKind === TRANSFORM_KIND_MATH
          ? "math"
          : String(key).toLowerCase() === CUSTOM_KEY || transformKind === TRANSFORM_KIND_CUSTOM
            ? "custom"
            : "predefined");
  const displayName =
    step.displayName ||
    step.customName ||
    step.CustomName ||
    step.transformDisplayName ||
    step.TransformDisplayName ||
    (kind === "custom"
      ? "Custom"
      : kind === LOOKUP_ENTITY_KEY
        ? "Entity Lookup"
        : kind === LOOKUP_SOURCE_KEY
          ? "Source Lookup"
          : kind === "math"
            ? (step.mathOperator || step.MathOperator || "Math")
            : key);
  return {
    id: step.id || `ts_${Date.now()}_${index}`,
    kind,
    transformKind:
      transformKind ||
      (kind === LOOKUP_ENTITY_KEY
        ? TRANSFORM_KIND_ENTITY_LOOKUP
        : kind === LOOKUP_SOURCE_KEY
          ? TRANSFORM_KIND_SOURCE_LOOKUP
          : kind === "custom"
            ? TRANSFORM_KIND_CUSTOM
            : kind === "math"
              ? TRANSFORM_KIND_MATH
              : TRANSFORM_KIND_PREDEFINED),
    key,
    displayName,
    code: kind === "custom" && !String(code || "").trim() ? DEFAULT_CUSTOM_TRANSFORM_SCRIPT : code,
    isActive: step?.isActive !== false && step?.IsActive !== false,
    lookupObjectType: getLookupObjectType(step, kind),
    lookupObjectId:
      step?.lookupObjectId ||
      step?.LookupObjectId ||
      step?.lookupEntityObjectId ||
      step?.LookupEntityObjectId ||
      step?.lookupSourceObjectId ||
      step?.LookupSourceObjectId ||
      null,
    lookupSearchFieldId:
      step?.lookupSearchFieldId ||
      step?.LookupSearchFieldId ||
      step?.lookupEntitySearchFieldId ||
      step?.LookupEntitySearchFieldId ||
      step?.lookupSourceSearchFieldId ||
      step?.LookupSourceSearchFieldId ||
      null,
    lookupResultFieldId:
      step?.lookupResultFieldId ||
      step?.LookupResultFieldId ||
      step?.lookupEntityResultFieldId ||
      step?.LookupEntityResultFieldId ||
      step?.lookupSourceResultFieldId ||
      step?.LookupSourceResultFieldId ||
      null,
    lookupMatchMethod: step?.lookupMatchMethod || step?.LookupMatchMethod || "==",
    lookupIgnoreCase: step?.lookupIgnoreCase !== false && step?.LookupIgnoreCase !== false,
    lookupNoMatchBehavior: step?.lookupNoMatchBehavior || step?.LookupNoMatchBehavior || "ReturnNull",
    lookupMultipleMatchBehavior: step?.lookupMultipleMatchBehavior || step?.LookupMultipleMatchBehavior || "First",
    lookupPageSize: Number(step?.lookupPageSize || step?.LookupPageSize || 1000) || 1000,
    lookupMaxPages: Number(step?.lookupMaxPages || step?.LookupMaxPages || 100) || 100,
    mathOperator: step?.mathOperator || step?.MathOperator || "Add",
    operandValue: step?.operandValue || step?.OperandValue || "",
    scale: step?.scale ?? step?.Scale ?? null,
    raw: step?.raw || step || null,
  };
};

const buildInitialPipeline = (mapItem) => {
  const fromSteps = (mapItem?.transformSteps || [])
    .map((step, idx) => normalizeStep(step, idx))
    .filter(Boolean);

  if (fromSteps.length > 0) return fromSteps;

  const key = mapItem?.transformKey;
  const code = (mapItem?.transformCode || "").trim();
  if (key && key !== CUSTOM_KEY) {
    return [{ id: `ts_${Date.now()}_0`, kind: "predefined", key, displayName: mapItem?.transformDisplayName || key, code: "" }];
  }
  if (code || key === CUSTOM_KEY) {
    return [{
      id: `ts_${Date.now()}_0`,
      kind: "custom",
      key: CUSTOM_KEY,
      displayName: "Custom",
      code: String(mapItem?.transformCode || "").trim() ? toEditableScriptBody(mapItem?.transformCode) : DEFAULT_CUSTOM_TRANSFORM_SCRIPT,
    }];
  }
  return [];
};

const TransformToolkitDialog = ({
  open,
  mapping,
  transforms,
  sourceField,
  sourceFields = [],
  entityField,
  sourceObjects = [],
  entityObjects = [],
  sourceObjectFieldMapItemMetadata,
  sourceObjectFieldTransformScriptItemMetadata,
  hideImportance = false,
  hideInputs = false,
  skipTypeMismatchWarning = false,
  titleText = "Transform Toolkit",
  deleteText = "Remove Mapping",
  embedded = false,
  showEditorTitle = false,
  editorRef,
  onCancel,
  onSave,
  onDelete,
  onValidationChange,
}) => {
  const [search, setSearch] = useState("");
  const [pipeline, setPipeline] = useState([]);
  const [transformFields, setTransformFields] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [constantValue, setConstantValue] = useState(mapping?.constantValue || mapping?.specialValue || "");
  const [importance, setImportance] = useState(255);
  const [legacyCustomScript, setLegacyCustomScript] = useState("");
  const [isMultiTransformMode, setIsMultiTransformMode] = useState(true);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [primarySourceFieldId, setPrimarySourceFieldId] = useState(sourceField?.id || mapping?.sourceFieldId || "");

  const isLookupKind = (kind) => kind === LOOKUP_SOURCE_KEY || kind === LOOKUP_ENTITY_KEY;

  useEffect(() => {
    const nextPipeline = buildInitialPipeline(mapping);
    const rawLegacyScript = String(mapping?.raw?.CustomTransformScript || mapping?.raw?.customTransformScript || "").trim();
    setPipeline(nextPipeline);
    setTransformFields(asArray(mapping?.transformFields).map((input, index) => ({
      id: input?.id || `input_${Date.now()}_${index}`,
      sourceObjectId: input?.sourceObjectId || mapping?.sourceObjectId || null,
      sourceFieldId: input?.sourceFieldId || "",
      fieldName: input?.fieldName || "",
      inputName: input?.inputName || "",
      sequence: Number(input?.sequence || index + 1),
      isActive: input?.isActive !== false,
      isDeleted: input?.isDeleted === true,
      raw: input?.raw || null,
    })));
    setSelectedIndex(nextPipeline.length > 0 ? 0 : -1);
    setSearch("");
    setConstantValue(mapping?.constantValue || mapping?.specialValue || "");
    setImportance(clampImportance(mapping?.importance ?? mapping?.raw?.Importance ?? 255));
    setPrimarySourceFieldId(sourceField?.id || mapping?.sourceFieldId || "");
    const initialLegacy = mapping?.raw?.CustomTransformScript || mapping?.raw?.customTransformScript || "";
    const editableLegacy = toEditableScriptBody(initialLegacy);
    setLegacyCustomScript(String(editableLegacy || "").trim() ? editableLegacy : DEFAULT_CUSTOM_TRANSFORM_SCRIPT);
    setIsMultiTransformMode(rawLegacyScript.length === 0);
  }, [mapping, sourceField?.id]);

  const selectedStep = selectedIndex >= 0 ? pipeline[selectedIndex] : null;
  const effectiveSourceField = useMemo(
    () =>
      asArray(sourceFields).find((field) => field?.id === primarySourceFieldId) ||
      (sourceField?.id === primarySourceFieldId || !primarySourceFieldId ? sourceField : null) ||
      sourceField,
    [primarySourceFieldId, sourceField, sourceFields]
  );
  const selectedMathOperator = selectedStep?.kind === "math" ? (selectedStep?.mathOperator || "Add") : "";
  const selectedMathOperatorLabel =
    asArray(MATH_OPERATORS).find((option) => option?.Value === selectedMathOperator)?.Text || selectedMathOperator || "Math";
  const mathUsesConstant = MATH_OPERATORS_REQUIRING_OPERAND.has(selectedMathOperator);
  const mathUsesScale = MATH_OPERATORS_REQUIRING_SCALE.has(selectedMathOperator);
  const mathConstantLabel = getMathConstantLabel(selectedMathOperator);
  const mathConstantPlaceholder = getMathConstantPlaceholder(selectedMathOperator);
  const mathScaleLabel = selectedMathOperator === "ImpliedDecimal" ? "Decimal Places" : "Scale";
  const mathScalePlaceholder = selectedMathOperator === "ImpliedDecimal" ? "2" : "0";
  const activeTransformFields = useMemo(
    () => asArray(transformFields).filter((input) => input?.sourceFieldId && input?.isDeleted !== true && input?.isActive !== false),
    [transformFields]
  );
  const visibleTransformFields = useMemo(
    () => asArray(transformFields).filter((input) => input?.isDeleted !== true),
    [transformFields]
  );
  const logicalTransformInputs = useMemo(() => {
    const inputs = [];
    const seen = new Set();
    const primaryId = String(effectiveSourceField?.id || "").trim();
    const addInput = ({ id, label, inputId = null, isPrimary = false }) => {
      const key = String(id || label || "").trim().toLowerCase();
      const cleanLabel = String(label || "").trim();
      if (!key || !cleanLabel || seen.has(key)) return;
      seen.add(key);
      inputs.push({
        key: isPrimary ? `primary_${inputId || key}` : `input_${inputId || key}`,
        id,
        inputId,
        label: cleanLabel,
        isPrimary,
      });
    };
    activeTransformFields.forEach((input) => {
      const field = asArray(sourceFields).find((item) => item?.id === input.sourceFieldId);
      const isPrimary = !!primaryId && String(input.sourceFieldId) === primaryId;
      addInput({
        id: input.sourceFieldId,
        inputId: input.id,
        label: field?.displayName || input?.displayName || input?.inputName || field?.fieldName || input?.fieldName || "",
        isPrimary,
      });
    });
    if (!mapping?.isConstant) {
      addInput({
        id: effectiveSourceField?.id,
        label: effectiveSourceField?.displayName || effectiveSourceField?.fieldName || "",
        isPrimary: true,
      });
    }
    return inputs;
  }, [activeTransformFields, effectiveSourceField?.displayName, effectiveSourceField?.fieldName, effectiveSourceField?.id, mapping?.isConstant, sourceFields]);
  const activeTransformFieldSummary = useMemo(
    () => logicalTransformInputs.map((input) => input.label).join(", "),
    [logicalTransformInputs]
  );
  const hasMultipleLogicalInputs = useMemo(() => {
    if (mapping?.isConstant) return false;
    return logicalTransformInputs.length > 1;
  }, [logicalTransformInputs.length, mapping?.isConstant]);
  const canRemoveSingleInput = logicalTransformInputs.length > 1;
  const sourceLookupObjects = useMemo(
    () => dedupeById(asArray(sourceObjects).filter((item) => item?.id)),
    [sourceObjects]
  );
  const entityLookupObjects = useMemo(
    () => dedupeById(asArray(entityObjects).filter((item) => item?.id)),
    [entityObjects]
  );
  const lookupObjectOptions = useMemo(() => {
    if (!selectedStep) return [];
    return selectedStep.kind === LOOKUP_ENTITY_KEY ? entityLookupObjects : sourceLookupObjects;
  }, [selectedStep, entityLookupObjects, sourceLookupObjects]);
  const selectedLookupObject = useMemo(
    () => lookupObjectOptions.find((item) => item.id === selectedStep?.lookupObjectId) || null,
    [lookupObjectOptions, selectedStep?.lookupObjectId]
  );
  const lookupFieldOptions = useMemo(
    () => dedupeById(asArray(selectedLookupObject?.fields).filter((field) => field?.id)),
    [selectedLookupObject]
  );
  const lookupObjectSelectData = useMemo(
    () => toSelectData(
      lookupObjectOptions,
      (item) => item.id,
      (item) => item.displayName || item.objectName || item.id
    ),
    [lookupObjectOptions]
  );
  const lookupFieldSelectData = useMemo(
    () => toSelectData(
      lookupFieldOptions,
      (field) => field.id,
      (field) => field.displayName || field.fieldName || field.id
    ),
    [lookupFieldOptions]
  );
  const sourceFieldSelectData = useMemo(
    () => toSelectData(
      dedupeById(asArray(sourceFields).filter((field) => field?.id)),
      (field) => field.id,
      (field) => field.displayName || field.fieldName || field.id
    ),
    [sourceFields]
  );
  const sourceFieldIdSet = useMemo(() => new Set(dedupeById(asArray(sourceFields).filter((field) => field?.id)).map((field) => field.id)), [sourceFields]);
  const getTransformFieldValidationWarnings = useCallback((input, index) => {
    if (!input || input?.isDeleted === true || input?.isActive === false) return [];
    const label = input?.inputName || input?.fieldName || `Input ${index + 1}`;
    if (!input?.sourceFieldId) return [`${label} needs a source field.`];
    if (!sourceFieldIdSet.has(input.sourceFieldId)) return [`${label} source field is no longer available.`];
    return [];
  }, [sourceFieldIdSet]);
  const transformFieldValidationWarnings = useMemo(
    () => asArray(transformFields).flatMap((input, index) => getTransformFieldValidationWarnings(input, index)),
    [getTransformFieldValidationWarnings, transformFields]
  );
  const lookupObjectFieldMetadata = useMemo(
    () =>
      buildSearchSelectMetadata({
        fieldName: "LookupObjectId",
        displayName: "Lookup Object",
        dataJson: lookupObjectSelectData,
        placeHolder: "Search objects...",
      }),
    [lookupObjectSelectData]
  );
  const lookupSearchFieldMetadata = useMemo(
    () =>
      buildSearchSelectMetadata({
        fieldName: "LookupSearchFieldId",
        displayName: "Search Field",
        dataJson: lookupFieldSelectData,
        placeHolder: "Search fields...",
      }),
    [lookupFieldSelectData]
  );
  const lookupResultFieldMetadata = useMemo(
    () =>
      buildSearchSelectMetadata({
        fieldName: "LookupResultFieldId",
        displayName: "Result Field",
        dataJson: lookupFieldSelectData,
        placeHolder: "Search fields...",
      }),
    [lookupFieldSelectData]
  );
  const lookupMatchFieldMetadata = useMemo(
    () =>
      buildSelectListMetadata({
        fieldName: "LookupMatchMethod",
        displayName: "Match Method",
        dataJson: toSelectData(LOOKUP_MATCH_OPTIONS, (option) => option.value, (option) => option.label),
      }),
    []
  );
  const lookupNoMatchFieldMetadata = useMemo(
    () =>
      buildSelectListMetadata({
        fieldName: "LookupNoMatchBehavior",
        displayName: "No Match",
        dataJson: toSelectData(LOOKUP_NO_MATCH_OPTIONS, (option) => option.value, (option) => option.label),
      }),
    []
  );
  const lookupMultipleMatchFieldMetadata = useMemo(
    () =>
      buildSelectListMetadata({
        fieldName: "LookupMultipleMatchBehavior",
        displayName: "Multiple Matches",
        dataJson: toSelectData(LOOKUP_MULTI_MATCH_OPTIONS, (option) => option.value, (option) => option.label),
      }),
    []
  );
  const lookupIgnoreCaseFieldMetadata = useMemo(
    () =>
      buildSelectListMetadata({
        fieldName: "LookupIgnoreCase",
        displayName: "Ignore Case",
        dataJson: [
          { Value: "true", Text: "Yes" },
          { Value: "false", Text: "No" },
        ],
      }),
    []
  );
  const lookupPageSizeFieldMetadata = useMemo(
    () => buildPositiveIntegerMetadata({ fieldName: "LookupPageSize", displayName: "Page Size" }),
    []
  );
const lookupMaxPagesFieldMetadata = useMemo(
    () => buildPositiveIntegerMetadata({ fieldName: "LookupMaxPages", displayName: "Max Pages" }),
    []
  );
  const getLookupObjectsForKind = useCallback(
    (kind) => (kind === LOOKUP_ENTITY_KEY ? entityLookupObjects : sourceLookupObjects),
    [entityLookupObjects, sourceLookupObjects]
  );
  const getLookupValidationError = useCallback((step) => {
    if (!isLookupKind(step?.kind)) return "";
    const objects = getLookupObjectsForKind(step.kind);
    const lookupLabel = step.kind === LOOKUP_ENTITY_KEY ? "Entity lookup" : "Source lookup";
    if (objects.length === 0) return `${lookupLabel} is unavailable because no lookup objects are available.`;
    if (!step?.lookupObjectId) return `${lookupLabel} requires a lookup object.`;

    const lookupObject = objects.find((item) => item?.id === step.lookupObjectId);
    if (!lookupObject) return `${lookupLabel} uses a lookup object that is no longer available.`;

    const fields = dedupeById(asArray(lookupObject?.fields).filter((field) => field?.id));
    if (fields.length === 0) return `${lookupLabel} lookup object has no selectable fields.`;
    if (!step?.lookupSearchFieldId) return `${lookupLabel} requires a search field.`;
    if (!fields.some((field) => field.id === step.lookupSearchFieldId)) return `${lookupLabel} search field is no longer available.`;
    if (!step?.lookupResultFieldId) return `${lookupLabel} requires a result field.`;
    if (!fields.some((field) => field.id === step.lookupResultFieldId)) return `${lookupLabel} result field is no longer available.`;
    return "";
  }, [getLookupObjectsForKind]);
  const getMathValidationErrors = useCallback((step) => {
    if (step?.kind !== "math" || step?.isDeleted === true || step?.isActive === false) return [];
    const warnings = [];
    const operator = String(step?.mathOperator || "").trim();
    if (!operator) {
      warnings.push("Math step requires an operation.");
      return warnings;
    }
    const supportedOperators = new Set(asArray(MATH_OPERATORS).map((option) => option?.Value));
    if (!supportedOperators.has(operator)) warnings.push(`Math operation '${operator}' is not supported.`);
    const needsConstant = MATH_OPERATORS_REQUIRING_OPERAND.has(operator) && !hasMultipleLogicalInputs;
    if (needsConstant && !String(step?.operandValue ?? "").trim()) warnings.push(`${step?.displayName || operator} requires a value.`);
    if ((operator === "Divide" || operator === "Modulo") && String(step?.operandValue ?? "").trim() && Number(step?.operandValue) === 0) {
      warnings.push(`${step?.displayName || operator} cannot use 0.`);
    }
    if (MATH_OPERATORS_REQUIRING_SCALE.has(operator) && step?.scale != null && step?.scale !== "" && !Number.isFinite(Number(step.scale))) {
      warnings.push(`${step?.displayName || operator} scale must be a number.`);
    }
    return warnings;
  }, [hasMultipleLogicalInputs]);
  const getStepValidationWarnings = useCallback((step) => {
    if (!step || step?.isDeleted === true || step?.isActive === false) return [];
    const warnings = [];
    if (step.kind === "custom") {
      if (!String(step.code || "").trim()) warnings.push("Custom step has empty code.");
      if (!String(step.displayName || "").trim()) warnings.push("Custom Name is required for custom step(s).");
    }
    const lookupWarning = getLookupValidationError(step);
    if (lookupWarning) warnings.push(lookupWarning);
    warnings.push(...getMathValidationErrors(step));
    return warnings;
  }, [getLookupValidationError, getMathValidationErrors]);
  const operationItemContext = useMemo(() => {
    const selectedCode =
      selectedStep?.kind === "custom"
        ? toStoredWrappedScript(selectedStep?.code || "")
        : (selectedStep?.code || "");
    return {
      ...(mapping?.raw || {}),
      SourceField: effectiveSourceField?.raw || effectiveSourceField || null,
      EntityField: entityField?.raw || entityField || null,
      CustomTransformScript: isMultiTransformMode ? selectedCode : toStoredWrappedScript(String(legacyCustomScript || "")),
      CustomName: selectedStep?.displayName || "",
    };
  }, [mapping?.raw, effectiveSourceField, entityField, isMultiTransformMode, legacyCustomScript, selectedStep?.code, selectedStep?.displayName, selectedStep?.kind]);
  const customJavaScriptFieldMetadata = useMemo(
    () =>
      findFieldMetadata(sourceObjectFieldTransformScriptItemMetadata, "CustomTransformScript") ||
      findFieldMetadata(sourceObjectFieldMapItemMetadata, "CustomTransformScript") || {
        FieldName: "CustomTransformScript",
        DisplayName: "Custom JavaScript",
        FieldType: SystemTypes.UIFieldType.JS,
      },
    [sourceObjectFieldMapItemMetadata, sourceObjectFieldTransformScriptItemMetadata]
  );
  const legacyMapCustomJavaScriptFieldMetadata = useMemo(
    () =>
      findFieldMetadata(sourceObjectFieldMapItemMetadata, "CustomTransformScript") || {
        FieldName: "CustomTransformScript",
        DisplayName: "Custom Transform",
        FieldType: SystemTypes.UIFieldType.JS,
      },
    [sourceObjectFieldMapItemMetadata]
  );
  const visibleMathOperators = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    if (!q) return MATH_OPERATORS;
    return MATH_OPERATORS.filter((option) => {
      const label = String(`${option?.Text || ""} ${option?.Value || ""} Math`).toLowerCase();
      return label.includes(q);
    });
  }, [search]);
  const visibleLookupKinds = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    const options = [
      {
        key: LOOKUP_SOURCE_KEY,
        label: "Source",
        disabled: sourceLookupObjects.length === 0,
        title: sourceLookupObjects.length === 0 ? "No source objects are available for lookup." : "Add a source-object lookup step.",
      },
      {
        key: LOOKUP_ENTITY_KEY,
        label: "Entity",
        disabled: entityLookupObjects.length === 0,
        title: entityLookupObjects.length === 0 ? "No entity objects are available for lookup." : "Add an entity-object lookup step.",
      },
    ];
    if (!q) return options;
    return options.filter((option) => String(`${option.label} Lookup`).toLowerCase().includes(q));
  }, [entityLookupObjects.length, search, sourceLookupObjects.length]);
  const filteredTransforms = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    const catalog = dedupeTransforms(transforms || []);
    if (!q) return catalog;
    return catalog.filter((t) => {
      const label = String(t.displayName || t.name || t.key || t.id || "").toLowerCase();
      return label.includes(q);
    });
  }, [search, transforms]);
  const groupedTransforms = useMemo(() => {
    const groups = [];
    const byGroup = new Map();

    filteredTransforms.forEach((transform) => {
      const groupName = String(transform?.group || transform?.Group || "Other").trim() || "Other";
      if (!byGroup.has(groupName)) {
        const entry = { group: groupName, items: [] };
        byGroup.set(groupName, entry);
        groups.push(entry);
      }
      byGroup.get(groupName).items.push(transform);
    });

    return groups;
  }, [filteredTransforms]);

  useEffect(() => {
    const nextGroups = new Set(
      dedupeTransforms(Array.isArray(transforms) ? transforms : []).map((t) => String(t?.group || t?.Group || "Other").trim() || "Other")
    );
    nextGroups.add("Math");
    nextGroups.add("Lookup");
    setCollapsedGroups((previous) => {
      const next = { ...previous };
      nextGroups.forEach((groupName) => {
        if (next[groupName] == null) next[groupName] = groupName === "Math" || groupName === "Lookup" ? false : true;
      });
      Object.keys(next).forEach((groupName) => {
        if (!nextGroups.has(groupName)) delete next[groupName];
      });
      return next;
    });
  }, [transforms]);

  const toggleGroupCollapsed = (groupName) => {
    setCollapsedGroups((previous) => {
      const currentlyCollapsed = previous[groupName] === true;
      const next = {};
      Object.keys(previous).forEach((name) => {
        next[name] = true;
      });
      next[groupName] = !currentlyCollapsed ? true : false;
      return next;
    });
  };

  const typeMismatch = useMemo(() => {
    if (skipTypeMismatchWarning) return false;
    if (mapping?.isConstant) return false;
    const sourceType = String(effectiveSourceField?.dataTypeId || effectiveSourceField?.dataTypeName || "").toLowerCase();
    const targetType = String(entityField?.dataTypeId || entityField?.dataTypeName || "").toLowerCase();
    if (!sourceType || !targetType) return false;
    return sourceType !== targetType;
  }, [mapping?.isConstant, skipTypeMismatchWarning, effectiveSourceField, entityField]);

  const pipelineHasEmptyCustom = useMemo(() => {
    return pipeline.some((step) => step?.isActive !== false && step.kind === "custom" && !String(step.code || "").trim());
  }, [pipeline]);
  const pipelineHasEmptyCustomName = useMemo(() => {
    return pipeline.some((step) => step?.isActive !== false && step.kind === "custom" && !String(step.displayName || "").trim());
  }, [pipeline]);
  const pipelineHasInvalidLookup = useMemo(() => {
    return pipeline.some((step) => step?.isActive !== false && Boolean(getLookupValidationError(step)));
  }, [getLookupValidationError, pipeline]);
  const pipelineHasInvalidMath = useMemo(() => {
    return pipeline.some((step) => getMathValidationErrors(step).length > 0);
  }, [getMathValidationErrors, pipeline]);
  const activePipelineLength = useMemo(
    () => pipeline.filter((step) => step?.isDeleted !== true && step?.isActive !== false).length,
    [pipeline]
  );
  const showTypeMismatchWarning = typeMismatch && activePipelineLength === 0;
  const showLegacyTypeMismatchWarning = typeMismatch && !String(legacyCustomScript || "").trim();
  const validationWarnings = useMemo(() => {
    const warnings = [];
    if (isMultiTransformMode) {
      if (showTypeMismatchWarning) warnings.push("Type mismatch: add at least one transform step.");
      warnings.push(...transformFieldValidationWarnings);
      pipeline.forEach((step) => warnings.push(...getStepValidationWarnings(step)));
    } else if (showLegacyTypeMismatchWarning) {
      warnings.push("Type mismatch: enter a custom transform script.");
    }
    return warnings;
  }, [getStepValidationWarnings, isMultiTransformMode, showTypeMismatchWarning, transformFieldValidationWarnings, showLegacyTypeMismatchWarning, pipeline]);

  const canApply = useMemo(() => {
    if (isMultiTransformMode) {
      if (pipelineHasEmptyCustom) return false;
      if (pipelineHasEmptyCustomName) return false;
      if (pipelineHasInvalidLookup) return false;
      if (pipelineHasInvalidMath) return false;
      if (transformFieldValidationWarnings.length > 0) return false;
      if (showTypeMismatchWarning) return false;
      return true;
    }
    if (showLegacyTypeMismatchWarning) return false;
    return true;
  }, [isMultiTransformMode, pipelineHasEmptyCustom, pipelineHasEmptyCustomName, pipelineHasInvalidLookup, pipelineHasInvalidMath, transformFieldValidationWarnings.length, showTypeMismatchWarning, showLegacyTypeMismatchWarning]);

  const addCustom = () => {
    const next = {
      id: `ts_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      kind: "custom",
      transformKind: TRANSFORM_KIND_CUSTOM,
      key: CUSTOM_KEY,
      displayName: "",
      code: DEFAULT_CUSTOM_TRANSFORM_SCRIPT,
      isActive: true,
    };
    setPipeline((prev) => [...prev, next]);
    setSelectedIndex(pipeline.length);
  };

  const addPredefined = (transform) => {
    const key = transform?.key || transform?.id;
    if (!key) return;
    const next = {
      id: `ts_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      kind: "predefined",
      transformKind: TRANSFORM_KIND_PREDEFINED,
      key,
      displayName: transform?.displayName || transform?.name || key,
      code: transform?.code || "",
      isActive: true,
    };
    setPipeline((prev) => [...prev, next]);
    setSelectedIndex(pipeline.length);
  };

  const addMath = (operator = "Add") => {
    const selectedOperator = operator || "Add";
    const selectedOperatorLabel = asArray(MATH_OPERATORS).find((option) => option?.Value === selectedOperator)?.Text || selectedOperator;
    const next = {
      id: `ts_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      kind: "math",
      transformKind: TRANSFORM_KIND_MATH,
      key: "Math",
      displayName: selectedOperatorLabel,
      code: "",
      mathOperator: selectedOperator,
      operandValue: "",
      scale: null,
      isActive: true,
    };
    setPipeline((prev) => [...prev, next]);
    setSelectedIndex(pipeline.length);
  };

  const addLookup = (kind) => {
    const useEntityObjects = kind === LOOKUP_ENTITY_KEY;
    const next = {
      id: `ts_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      kind,
      transformKind: useEntityObjects ? TRANSFORM_KIND_ENTITY_LOOKUP : TRANSFORM_KIND_SOURCE_LOOKUP,
      key: null,
      displayName: useEntityObjects ? "Entity Lookup" : "Source Lookup",
      code: "",
      isActive: true,
      lookupObjectType: useEntityObjects ? "EntityObject" : "SourceObject",
      lookupObjectId: null,
      lookupSearchFieldId: null,
      lookupResultFieldId: null,
      lookupMatchMethod: "==",
      lookupIgnoreCase: true,
      lookupNoMatchBehavior: "ReturnNull",
      lookupMultipleMatchBehavior: "First",
      lookupPageSize: 1000,
      lookupMaxPages: 100,
    };
    setPipeline((prev) => [...prev, next]);
    setSelectedIndex(pipeline.length);
  };

  const updateSelectedStep = (patch) => {
    if (selectedIndex < 0) return;
    setPipeline((prev) => prev.map((step, idx) => (idx === selectedIndex ? { ...step, ...patch } : step)));
  };

  const removeAt = (index) => {
    if (index < 0) return;
    setPipeline((prev) => {
      const next = prev.slice();
      next.splice(index, 1);
      if (next.length === 0) {
        setSelectedIndex(-1);
      } else if (selectedIndex >= next.length) {
        setSelectedIndex(next.length - 1);
      } else if (selectedIndex === index) {
        setSelectedIndex(Math.max(0, index - 1));
      }
      return next;
    });
  };

  const removeTransformInput = useCallback((inputId, sourceFieldId = null) => {
    const removedSourceFieldId = sourceFieldId || activeTransformFields.find((input) => input?.id === inputId)?.sourceFieldId || null;
    if (removedSourceFieldId && String(removedSourceFieldId) === String(primarySourceFieldId || "")) {
      const replacement = activeTransformFields.find(
        (input) => input?.id !== inputId && input?.sourceFieldId && String(input.sourceFieldId) !== String(removedSourceFieldId)
      );
      if (replacement?.sourceFieldId) setPrimarySourceFieldId(replacement.sourceFieldId);
    }
    if (!inputId) return;
    setTransformFields((previous) =>
      previous.map((entry) =>
        entry.id === inputId
          ? { ...entry, isDeleted: true, isActive: false }
          : entry
      )
    );
  }, [activeTransformFields, primarySourceFieldId]);

  const moveAt = (index, direction) => {
    const target = index + direction;
    if (index < 0 || target < 0 || target >= pipeline.length) return;
    setPipeline((prev) => {
      const next = prev.slice();
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
    if (selectedIndex === index) setSelectedIndex(target);
    else if (selectedIndex === target) setSelectedIndex(index);
  };

  const handleSave = useCallback(async () => {
    if (!canApply) return { Success: false };

    const effectivePipeline = isMultiTransformMode ? pipeline : [];
    const first = effectivePipeline[0] || null;
    const transformKey = first ? first.key : null;
    const transformDisplayName = first ? first.displayName : "";
    const effectiveStoredPipeline = effectivePipeline.map((step) => ({
      ...step,
      code: step?.kind === "custom" ? toStoredWrappedScript(step?.code || "") : (step?.code || ""),
    }));
    const transformCode = isMultiTransformMode
      ? (effectiveStoredPipeline[0] ? (effectiveStoredPipeline[0].code || "") : "")
      : toStoredWrappedScript(String(legacyCustomScript || ""));

    const patch = {
      sourceFieldId: primarySourceFieldId || mapping?.sourceFieldId || sourceField?.id || null,
      transformKey,
      transformDisplayName,
      transformCode,
      transformSteps: effectiveStoredPipeline.map((step) => ({ ...step })),
      multiTransformMode: isMultiTransformMode,
      legacyCustomTransformScript: isMultiTransformMode ? "" : toStoredWrappedScript(String(legacyCustomScript || "")),
      transformFields: asArray(transformFields)
        .filter((input) => input?.sourceFieldId && input?.isDeleted !== true)
        .map((input, index) => ({
          ...input,
          sequence: index + 1,
          inputName: input?.inputName || "",
        })),
      importance: clampImportance(importance),
      constantValue: mapping?.isConstant ? constantValue : undefined,
    };

    onSave(patch);
    return { Success: true, patch };
  }, [canApply, constantValue, importance, isMultiTransformMode, legacyCustomScript, mapping?.isConstant, mapping?.sourceFieldId, onSave, pipeline, primarySourceFieldId, sourceField?.id, transformFields]);

  useImperativeHandle(editorRef, () => ({
    save: handleSave,
    canApply,
    validationWarnings,
  }), [canApply, handleSave, validationWarnings]);

  useEffect(() => {
    if (typeof onValidationChange !== "function") return;
    onValidationChange({ canApply, warnings: validationWarnings });
  }, [canApply, onValidationChange, validationWarnings]);

  const selectedStepWarnings = useMemo(
    () => getStepValidationWarnings(selectedStep),
    [getStepValidationWarnings, selectedStep]
  );
  const selectedLookupObjectInvalid = isLookupKind(selectedStep?.kind) && (!selectedStep?.lookupObjectId || getLookupValidationError(selectedStep).includes("lookup object"));
  const selectedLookupSearchInvalid = isLookupKind(selectedStep?.kind) && (!selectedStep?.lookupSearchFieldId || getLookupValidationError(selectedStep).includes("search field"));
  const selectedLookupResultInvalid = isLookupKind(selectedStep?.kind) && (!selectedStep?.lookupResultFieldId || getLookupValidationError(selectedStep).includes("result field"));
  const selectedMathWarnings = useMemo(
    () => getMathValidationErrors(selectedStep),
    [getMathValidationErrors, selectedStep]
  );

  const titleNode = (
    <div className="efmd-transform-dialog-title">
          <span>{titleText}</span>
          <label className="efmd-transform-mode-toggle" title="Toggle between multi-step transform scripts and legacy single custom transform script.">
            <input
              type="checkbox"
              checked={isMultiTransformMode}
              onChange={(event) => {
                const checked = event.target.checked;
                setIsMultiTransformMode(checked);
                if (checked && pipeline.length === 0 && String(legacyCustomScript || "").trim()) {
                  setPipeline([
                    {
                      id: `ts_${Date.now()}_legacy`,
                      kind: "custom",
                      key: CUSTOM_KEY,
                      displayName: "Custom",
                      code: legacyCustomScript,
                    },
                  ]);
                  setSelectedIndex(0);
                }
              }}
            />
            <span>Use Pipeline</span>
          </label>
        </div>
  );

  const contentNode = (
      <div className="efmd-dialog">
        <div className="efmd-transform-header">
          <div className="efmd-transform-header-field">
            {hasMultipleLogicalInputs
              ? <span className="efmd-transform-header-field-name">Multi Input</span>
              : getHeaderFieldLabel(effectiveSourceField, mapping?.isConstant)}
          </div>
          <div className="efmd-transform-arrow" title="Maps source to entity">{"->"}</div>
          <div className="efmd-transform-header-field">{getHeaderFieldLabel(entityField, false)}</div>
          {!hideImportance && (
            <div className="efmd-transform-importance-row">
              <div
                className="efmd-transform-title"
                title="Importance controls how mappings across different source objects are prioritized together. Lower numbers are more important and have more weight when determining the best representation of data."
              >
                Importance (1-255)
              </div>
              <input
                type="number"
                min={1}
                max={255}
                className="efmd-transform-input efmd-transform-input--importance"
                title="Importance controls how mappings across different source objects are prioritized together. Lower numbers are more important and have more weight when determining the best representation of data."
                value={importance}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  if (nextValue === "") {
                    setImportance(255);
                    return;
                  }
                  setImportance(clampImportance(nextValue));
                }}
              />
            </div>
          )}
        </div>

        {mapping?.isConstant && (
          <div>
            <div className="efmd-transform-title">Constant Value</div>
            <input className="efmd-transform-input" value={constantValue} onChange={(event) => setConstantValue(event.target.value)} />
          </div>
        )}

        {validationWarnings.length > 0 && <div className="efmd-transform-warn">{validationWarnings.join(" ")}</div>}

        {isMultiTransformMode && !mapping?.isConstant && hideInputs && activeTransformFields.length > 0 && (
          <div className="efmd-transform-inputs-hint efmd-transform-inputs-hint--drawn">
            <span className="efmd-transform-inputs-label">Inputs:</span>
            <span className="efmd-transform-input-chips">
              {(logicalTransformInputs.length > 0 ? logicalTransformInputs : [{ key: "fallback", label: activeTransformFieldSummary || `${activeTransformFields.length} input${activeTransformFields.length === 1 ? "" : "s"}`, isPrimary: true }]).map((input) => (
                <span className="efmd-transform-input-chip" key={input.key}>
                  <span>{input.label}</span>
                  {canRemoveSingleInput && (
                    <button
                      type="button"
                      className="efmd-transform-input-chip-remove"
                      aria-label={`Remove input ${input.label}`}
                      title={`Remove ${input.label}`}
                      onClick={() => removeTransformInput(input.inputId, input.id)}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </span>
              ))}
            </span>
          </div>
        )}

        {isMultiTransformMode && !mapping?.isConstant && !hideInputs && (
          <div className="efmd-transform-inputs-editor">
            <div className="efmd-transform-title-row">
              <div className="efmd-transform-title">Inputs</div>
              <button
                type="button"
                className="efmd-transform-btn"
                disabled={sourceFieldSelectData.length === 0}
                onClick={() =>
                  setTransformFields((previous) => [
                    ...previous,
                    {
                      id: `input_${Date.now()}_${Math.random().toString(16).slice(2)}`,
                      sourceObjectId: mapping?.sourceObjectId || null,
                      sourceFieldId: "",
                      fieldName: "",
                      inputName: "",
                      sequence: previous.length + 1,
                      isActive: true,
                      isDeleted: false,
                    },
                  ])
                }
              >
                + Input
              </button>
            </div>
            {visibleTransformFields.length === 0 ? (
              <div className="efmd-transform-inputs-hint">Custom JavaScript can use value, row, loopItem, loopIndex, groupKey, and namedInputs. Add inputs for multi-field formulas.</div>
            ) : (
              <div className="efmd-transform-inputs-list">
                {visibleTransformFields.map((input, index) => {
                  const inputWarnings = getTransformFieldValidationWarnings(input, index);
                  return (
                  <div className={`efmd-transform-input-row${inputWarnings.length > 0 ? " is-invalid" : ""}`} key={input.id} title={inputWarnings.join(" ")}>
                    <input
                      className="efmd-transform-input"
                      value={input.inputName || ""}
                      placeholder={`Input${index + 1}`}
                      onChange={(event) =>
                        setTransformFields((previous) => previous.map((entry) => (entry.id === input.id ? { ...entry, inputName: event.target.value } : entry)))
                      }
                    />
                    <select
                      className="efmd-select"
                      value={input.sourceFieldId || ""}
                      onChange={(event) => {
                        const nextFieldId = event.target.value || "";
                        const selected = asArray(sourceFields).find((field) => field.id === nextFieldId);
                        setTransformFields((previous) =>
                          previous.map((entry) =>
                            entry.id === input.id
                              ? {
                                  ...entry,
                                  sourceFieldId: nextFieldId,
                                  fieldName: selected?.fieldName || selected?.displayName || "",
                                }
                              : entry
                          )
                        );
                      }}
                    >
                      <option value="">Select source field</option>
                      {sourceFieldSelectData.map((option) => <option key={option.Value} value={option.Value}>{option.Text}</option>)}
                    </select>
                    {visibleTransformFields.length > 1 && (
                      <button
                        type="button"
                        className="efmd-transform-input-row-remove"
                        aria-label={`Remove input ${input.inputName || input.fieldName || `Input ${index + 1}`}`}
                        title={`Remove ${input.inputName || input.fieldName || `Input ${index + 1}`}`}
                        onClick={() => removeTransformInput(input.id)}
                      >
                        <TrashIcon />
                      </button>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {isMultiTransformMode ? (
          <div className="efmd-transform-grid">
            <div className="efmd-transform-pane efmd-transform-pane--add">
              <div className="efmd-transform-title-row">
                <div className="efmd-transform-title">Add Step</div>
                <div className="efmd-transform-actions">
                  <button type="button" className="efmd-transform-btn" onClick={addCustom}>Add Custom</button>
                </div>
              </div>
              <input
                className="efmd-transform-input"
                value={search}
                placeholder="Search transforms..."
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className="efmd-transform-list efmd-transform-list--tree">
                {visibleMathOperators.length === 0 && visibleLookupKinds.length === 0 && groupedTransforms.length === 0 && (
                  <div className="efmd-transform-tree-empty">No transforms found.</div>
                )}
                {visibleMathOperators.length > 0 && (
                  <div className="efmd-transform-tree-group" key="builtin-Math">
                    <button
                      type="button"
                      className="efmd-transform-tree-group-header"
                      onClick={() => toggleGroupCollapsed("Math")}
                    >
                      <TriangleIcon className={`efmd-transform-tree-group-caret${collapsedGroups.Math ? " is-collapsed" : ""}`} />
                      <span className="efmd-transform-tree-group-name">Math</span>
                      <span className="efmd-transform-tree-group-count">({visibleMathOperators.length})</span>
                    </button>
                    {!collapsedGroups.Math && (
                      <div className="efmd-transform-tree-children">
                        {visibleMathOperators.map((option) => (
                          <button
                            key={option.Value}
                            type="button"
                            className="efmd-transform-item efmd-transform-tree-item"
                            title={`Add ${option.Text} math step.`}
                            onClick={() => addMath(option.Value)}
                          >
                            {option.Text}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {visibleLookupKinds.length > 0 && (
                  <div className="efmd-transform-tree-group" key="builtin-Lookup">
                    <button
                      type="button"
                      className="efmd-transform-tree-group-header"
                      onClick={() => toggleGroupCollapsed("Lookup")}
                    >
                      <TriangleIcon className={`efmd-transform-tree-group-caret${collapsedGroups.Lookup ? " is-collapsed" : ""}`} />
                      <span className="efmd-transform-tree-group-name">Lookup</span>
                      <span className="efmd-transform-tree-group-count">({visibleLookupKinds.length})</span>
                    </button>
                    {!collapsedGroups.Lookup && (
                      <div className="efmd-transform-tree-children">
                        {visibleLookupKinds.map((option) => (
                          <button
                            key={option.key}
                            type="button"
                            className="efmd-transform-item efmd-transform-tree-item"
                            title={option.title}
                            disabled={option.disabled}
                            onClick={() => addLookup(option.key)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {groupedTransforms.map((groupEntry) => {
                  const groupName = groupEntry.group;
                  const isCollapsed = collapsedGroups[groupName] === true;
                  return (
                    <div className="efmd-transform-tree-group" key={groupName}>
                      <button
                        type="button"
                        className="efmd-transform-tree-group-header"
                        onClick={() => toggleGroupCollapsed(groupName)}
                      >
                        <TriangleIcon className={`efmd-transform-tree-group-caret${isCollapsed ? " is-collapsed" : ""}`} />
                        <span className="efmd-transform-tree-group-name">{groupName}</span>
                        <span className="efmd-transform-tree-group-count">({groupEntry.items.length})</span>
                      </button>
                      {!isCollapsed && (
                        <div className="efmd-transform-tree-children">
                          {groupEntry.items.map((transform) => (
                            <button
                              key={transform.key || transform.id}
                              type="button"
                              className="efmd-transform-item efmd-transform-tree-item"
                              title={String(transform?.description || transform?.Description || transform?.displayName || transform?.name || transform?.key || "")}
                              onClick={() => addPredefined(transform)}
                            >
                              {transform.displayName || transform.name || transform.key || transform.id}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="efmd-transform-pane efmd-transform-pane--pipeline">
              <div className="efmd-transform-title">Pipeline ({pipeline.length === 0 ? 1 : pipeline.length})</div>
              <div className="efmd-transform-list">
                {pipeline.length === 0 && (
                  <div className="efmd-transform-item is-row efmd-transform-item--placeholder">
                    <span title="Passthrough means no transform steps are applied; the input value is used as-is.">
                      Passthrough
                    </span>
                  </div>
                )}
                {pipeline.map((step, idx) => {
                  const stepWarnings = getStepValidationWarnings(step);
                  return (
                  <div
                    key={step.id}
                    className={`efmd-transform-item is-row ${selectedIndex === idx ? "is-selected" : ""} ${stepWarnings.length > 0 ? "is-invalid" : ""} ${step?.isActive === false ? "is-inactive" : ""}`}
                    title={stepWarnings.join(" ")}
                    onClick={() => setSelectedIndex(idx)}
                  >
                    <span className="efmd-transform-step-main">
                      <span className="efmd-transform-step-index">{idx + 1}.</span>
                      <input
                        type="text"
                        className="efmd-transform-pipeline-name-input"
                        value={step.displayName || ""}
                        placeholder={step.kind === "custom" ? "(Required)" : String(step.key || "")}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedIndex(idx);
                        }}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setPipeline((prev) => prev.map((entry, entryIdx) => (entryIdx === idx ? { ...entry, displayName: nextValue } : entry)));
                        }}
                      />
                    </span>
                    <span className="efmd-transform-inline-actions">
                      <label className="efmd-transform-active-toggle" title="Enable or disable this transform step.">
                        <input
                          type="checkbox"
                          checked={step?.isActive !== false}
                          onChange={(event) => {
                            event.stopPropagation();
                            const checked = event.target.checked;
                            setPipeline((prev) => prev.map((entry, entryIdx) => (entryIdx === idx ? { ...entry, isActive: checked } : entry)));
                          }}
                        />
                      </label>
                      <button type="button" className="efmd-transform-btn efmd-transform-btn--icon" title="Move up" aria-label="Move step up" onClick={(event) => { event.stopPropagation(); moveAt(idx, -1); }}><ArrowUpIcon /></button>
                      <button type="button" className="efmd-transform-btn efmd-transform-btn--icon" title="Move down" aria-label="Move step down" onClick={(event) => { event.stopPropagation(); moveAt(idx, 1); }}><ArrowDownIcon /></button>
                      <button type="button" className="efmd-transform-btn efmd-transform-btn--icon efmd-transform-btn--danger" title="Remove step" onClick={(event) => { event.stopPropagation(); removeAt(idx); }}>x</button>
                    </span>
                  </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div>
            {/* AI generation is handled by the JS FieldControl configuration below; do not render a second AI prompt here. */}
            <FieldControl
              className="efmd-transform-code-control"
              fieldMetadata={legacyMapCustomJavaScriptFieldMetadata}
              updatedItem={operationItemContext}
              value={legacyCustomScript}
              onChange={(value) => setLegacyCustomScript(value)}
            />
          </div>
        )}

        {isMultiTransformMode && selectedStep?.kind === "custom" && (
          <div className={selectedStepWarnings.length > 0 ? "is-invalid" : ""}>
            {/* AI generation is handled by the JS FieldControl configuration below; do not render a second AI prompt here. */}
            <FieldControl
              className="efmd-transform-code-control"
              fieldMetadata={customJavaScriptFieldMetadata}
              updatedItem={operationItemContext}
              value={selectedStep.code || ""}
              onChange={(value) => {
                setPipeline((prev) => prev.map((step, idx) => (idx === selectedIndex ? { ...step, code: value } : step)));
              }}
            />
          </div>
        )}

        {isMultiTransformMode && selectedStep?.kind === "math" && (
          <div className={`efmd-transform-math-editor${selectedMathWarnings.length > 0 ? " is-invalid" : ""}`}>
            <div className="efmd-transform-math-field">
              <span>Math Step</span>
              <div className="efmd-transform-math-readonly">{selectedMathOperatorLabel}</div>
            </div>
            {mathUsesConstant && (
              <label className={`efmd-transform-math-field${selectedMathWarnings.some((warning) => warning.includes("requires a value") || warning.includes("cannot use 0")) ? " is-invalid" : ""}`}>
                <span>{mathConstantLabel}</span>
                <input
                  className="efmd-transform-input"
                  value={selectedStep?.operandValue || ""}
                  placeholder={mathConstantPlaceholder}
                  onChange={(event) => updateSelectedStep({ operandValue: event.target.value })}
                />
              </label>
            )}
            {mathUsesScale && (
              <label className={`efmd-transform-math-field${selectedMathWarnings.some((warning) => warning.includes("scale must be a number")) ? " is-invalid" : ""}`}>
                <span>{mathScaleLabel}</span>
                <input
                  type="number"
                  className="efmd-transform-input"
                  value={selectedStep?.scale ?? ""}
                  placeholder={mathScalePlaceholder}
                  onChange={(event) => updateSelectedStep({ scale: event.target.value === "" ? null : Number(event.target.value) })}
                />
              </label>
            )}
          </div>
        )}

        {isMultiTransformMode && isLookupKind(selectedStep?.kind) && (
          <div className="efmd-transform-lookup-editor">
            <div className="efmd-transform-lookup-grid">
              <div className={`efmd-transform-lookup-field${selectedLookupObjectInvalid ? " is-invalid" : ""}`}>
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupObjectFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupObjectId || ""}
                  disabled={lookupObjectOptions.length === 0}
                  disableValidation={true}
                  onChange={(value) => {
                    const nextObjectId = value || null;
                    updateSelectedStep({
                      lookupObjectType: selectedStep?.kind === LOOKUP_ENTITY_KEY ? "EntityObject" : "SourceObject",
                      lookupObjectId: nextObjectId,
                      lookupSearchFieldId: null,
                      lookupResultFieldId: null,
                    });
                  }}
                />
              </div>

              <div className={`efmd-transform-lookup-field${selectedLookupSearchInvalid ? " is-invalid" : ""}`}>
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupSearchFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupSearchFieldId || ""}
                  disabled={lookupFieldOptions.length === 0}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupSearchFieldId: value || null })}
                />
              </div>

              <div className={`efmd-transform-lookup-field${selectedLookupResultInvalid ? " is-invalid" : ""}`}>
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupResultFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupResultFieldId || ""}
                  disabled={lookupFieldOptions.length === 0}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupResultFieldId: value || null })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupMatchFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupMatchMethod || "=="}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupMatchMethod: value || "==" })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupNoMatchFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupNoMatchBehavior || "ReturnNull"}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupNoMatchBehavior: value || "ReturnNull" })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupMultipleMatchFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupMultipleMatchBehavior || "First"}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupMultipleMatchBehavior: value || "First" })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupPageSizeFieldMetadata}
                  updatedItem={operationItemContext}
                  value={String(selectedStep?.lookupPageSize || 1000)}
                  disableValidation={true}
                  onChange={(value) =>
                    updateSelectedStep({ lookupPageSize: Math.max(1, Number(value || 1000) || 1000) })
                  }
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupIgnoreCaseFieldMetadata}
                  updatedItem={operationItemContext}
                  value={selectedStep?.lookupIgnoreCase !== false ? "true" : "false"}
                  disableValidation={true}
                  onChange={(value) => updateSelectedStep({ lookupIgnoreCase: String(value) !== "false" })}
                />
              </div>

              <div className="efmd-transform-lookup-field">
                <FieldControl
                  className="efmd-transform-lookup-control"
                  fieldMetadata={lookupMaxPagesFieldMetadata}
                  updatedItem={operationItemContext}
                  value={String(selectedStep?.lookupMaxPages || 100)}
                  disableValidation={true}
                  onChange={(value) =>
                    updateSelectedStep({ lookupMaxPages: Math.max(1, Number(value || 100) || 100) })
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
  );

  if (embedded) {
    return (
      <div className={`efmd-transform-embedded${canApply ? "" : " is-apply-disabled"}`}>
        {showEditorTitle && titleNode}
        {contentNode}
      </div>
    );
  }

  return (
    <DialogControl
      openDialog={open}
      title={titleNode}
      className={`efmd-transform-dialog${canApply ? "" : " is-apply-disabled"}`}
      onX={onCancel}
      onCancel={onCancel}
      onOk={handleSave}
      okText={"Apply"}
      disableOk={!canApply}
      customCenterText={deleteText}
      customCenterType={"cancel"}
      onCustomCenter={() => {
        onDelete();
        return { Success: true };
      }}
    >
      {contentNode}
    </DialogControl>
  );
};

export default TransformToolkitDialog;
